import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, FileText, Download, Eye, ChevronRight, 
  CheckCircle, Loader2, Mail, Phone, X, Search,
  FileCheck, ClipboardList, ScrollText, BookOpen,
  FileWarning, Lock, HelpCircle, Package, ArrowLeft,
  Calendar, DollarSign, Car, ExternalLink, Sparkles,
  FolderOpen, Clock, AlertCircle
} from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import { getUserFriendlyError } from '../utils/errorMessages';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date as MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Document metadata with descriptions and categories
const documentMetadata = {
  'welcome_letter': {
    icon: FileCheck,
    color: 'blue',
    category: 'essential',
    description: 'Your personalized welcome and policy overview'
  },
  'declarations': {
    icon: ClipboardList,
    color: 'purple',
    category: 'essential',
    description: 'Complete policy declarations and coverages'
  },
  'coverage_summary': {
    icon: Shield,
    color: 'green',
    category: 'coverage',
    description: 'Summary of your protection and limits'
  },
  'policy_form': {
    icon: ScrollText,
    color: 'indigo',
    category: 'coverage',
    description: 'Full policy terms and conditions'
  },
  'endorsements': {
    icon: BookOpen,
    color: 'amber',
    category: 'coverage',
    description: 'Policy endorsements and modifications'
  },
  'state_notices': {
    icon: FileText,
    color: 'slate',
    category: 'legal',
    description: 'Required state-specific notices'
  },
  'complaint_notice': {
    icon: AlertCircle,
    color: 'orange',
    category: 'legal',
    description: 'How to file a complaint with LossPay or your state regulator'
  },
  'fraud_warning': {
    icon: FileWarning,
    color: 'red',
    category: 'legal',
    description: 'Insurance fraud warning notices'
  },
  'privacy_notice': {
    icon: Lock,
    color: 'emerald',
    category: 'legal',
    description: 'How we protect your information'
  },
  'claims_guide': {
    icon: HelpCircle,
    color: 'cyan',
    category: 'essential',
    description: 'Step-by-step claims filing guide'
  },
  'full_packet': {
    icon: Package,
    color: 'blue',
    category: 'essential',
    description: 'Complete policy documentation package'
  }
};

const colorClasses = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:bg-blue-50' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', hover: 'hover:bg-purple-50' },
  green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', hover: 'hover:bg-green-50' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:bg-indigo-50' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', hover: 'hover:bg-amber-50' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200', hover: 'hover:bg-orange-50' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', hover: 'hover:bg-slate-50' },
  red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200', hover: 'hover:bg-red-50' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'hover:bg-emerald-50' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', hover: 'hover:bg-cyan-50' }
};

// Enhanced PDF Viewer Modal
function DocumentViewerModal({ isOpen, onClose, document: docProp, documentUrl, allDocuments, onSelectDocument, onDownload }) {
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Handle viewport height for iOS Safari
  useEffect(() => {
    const updateDimensions = () => {
      setViewportHeight(window.innerHeight);
      setIsMobile(window.innerWidth < 768);
    };
    
    if (isOpen) {
      // Lock body scroll
      window.document.body.style.overflow = 'hidden';
      window.document.body.style.position = 'fixed';
      window.document.body.style.width = '100%';
      window.document.body.style.top = `-${window.scrollY}px`;
      
      // Update dimensions on resize (handles iOS Safari address bar)
      window.addEventListener('resize', updateDimensions);
      updateDimensions();
      
      return () => {
        const scrollY = window.document.body.style.top;
        window.document.body.style.overflow = '';
        window.document.body.style.position = '';
        window.document.body.style.width = '';
        window.document.body.style.top = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
        window.removeEventListener('resize', updateDimensions);
      };
    }
  }, [isOpen]);

  if (!isOpen || !docProp) return null;

  const meta = documentMetadata[docProp.document_type] || { icon: FileText, color: 'blue' };
  const Icon = meta.icon;
  const colors = colorClasses[meta.color];
  
  // Calculate modal height (accounting for safe areas on iOS)
  const modalHeight = Math.min(viewportHeight * 0.92, viewportHeight - 32);
  
  // Full PDF URL for external viewing
  const fullPdfUrl = `${documentUrl}?inline=true`;
  
  // Open PDF in new tab (best for mobile)
  const handleOpenInNewTab = () => {
    window.open(fullPdfUrl, '_blank');
  };

  return (
    <div 
      className="fixed z-50 overflow-hidden"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: `${viewportHeight}px`,
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        padding: isMobile ? '4px' : '8px',
        paddingTop: isMobile ? '4px' : '8px',
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute bg-black/70 backdrop-blur-sm"
        style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      />
      
      {/* Modal Container - iOS safe */}
      <div 
        className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
        style={{ 
          height: isMobile ? `${viewportHeight - 8}px` : `${modalHeight}px`,
          maxHeight: isMobile ? `${viewportHeight - 8}px` : `${modalHeight}px`,
        }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b bg-white flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${colors.bg} rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0`}>
              <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${colors.text}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-slate-900 text-sm sm:text-lg truncate">{docProp.display_name}</h3>
              <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">{meta.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Open in new tab button - especially useful for mobile */}
            <Button
              variant="outline"
              onClick={handleOpenInNewTab}
              className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 md:hidden"
              title="Open in full screen"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => onDownload(docProp)}
              className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2"
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Download</span>
            </Button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg sm:rounded-xl transition-colors"
              aria-label="Close viewer"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>
          {/* Sidebar - Document List (hidden on mobile/tablet) */}
          <div className="hidden lg:block w-64 border-r bg-slate-50 overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">All Documents</p>
              <div className="space-y-1">
                {allDocuments.filter(d => d.document_type !== 'full_packet').map((doc) => {
                  const docMeta = documentMetadata[doc.document_type] || { icon: FileText, color: 'blue' };
                  const DocIcon = docMeta.icon;
                  const docColors = colorClasses[docMeta.color];
                  const isActive = doc.document_type === docProp.document_type;
                  
                  return (
                    <button
                      key={doc.document_type}
                      onClick={() => onSelectDocument(doc)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                        isActive 
                          ? 'bg-white shadow-sm border border-slate-200' 
                          : 'hover:bg-white/70'
                      }`}
                    >
                      <div className={`w-8 h-8 ${docColors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <DocIcon className={`h-4 w-4 ${docColors.text}`} />
                      </div>
                      <span className={`text-sm truncate ${isActive ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                        {doc.display_name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* PDF Viewer Area */}
          <div 
            className="flex-1 bg-slate-100 flex flex-col"
            style={{ 
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {/* Mobile hint banner */}
            {isMobile && (
              <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center justify-between">
                <p className="text-xs text-blue-700">
                  Trouble viewing? Tap the arrow icon to open full screen
                </p>
                <button
                  onClick={handleOpenInNewTab}
                  className="text-xs text-blue-600 font-medium flex items-center gap-1"
                >
                  Open <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {/* PDF embed - using Google Docs viewer for better mobile compatibility */}
            <div 
              style={{ 
                flex: 1,
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#f1f5f9',
              }}
            >
              {isMobile ? (
                // On mobile, use Google Docs Viewer for better rendering
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(documentUrl)}&embedded=true`}
                  title={docProp.display_name}
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              ) : (
                // On desktop, use native PDF rendering
                <iframe
                  src={`${documentUrl}?inline=true#page=1&zoom=page-width`}
                  title={docProp.display_name}
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Document Card Component
function DocumentCard({ document, onView, onDownload }) {
  const meta = documentMetadata[document.document_type] || { icon: FileText, color: 'blue', description: '' };
  const Icon = meta.icon;
  const colors = colorClasses[meta.color];

  return (
    <div 
      className={`group bg-white rounded-2xl border ${colors.border} shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer`}
      onClick={() => onView(document)}
      data-testid={`document-card-${document.document_type}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
            <Icon className={`h-6 w-6 ${colors.text}`} />
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); onView(document); }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="View"
            >
              <Eye className="h-4 w-4 text-slate-500" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(document); }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>
        <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-[#3B5BDB] transition-colors">
          {document.display_name}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2">
          {meta.description}
        </p>
      </div>
      <div className={`px-5 py-3 bg-slate-50 border-t ${colors.border} flex items-center justify-between`}>
        <span className="text-xs text-slate-400 flex items-center gap-1">
          <FileText className="h-3 w-3" />
          PDF Document
        </span>
        <span className="text-xs font-medium text-[#3B5BDB] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          View Document
          <ChevronRight className="h-3 w-3" />
        </span>
      </div>
    </div>
  );
}

export default function DocumentCenterPage() {
  const { policyNumber } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  
  const [policy, setPolicy] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchPolicyDetails = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const response = await fetch(`${API_URL}/api/policy/details/${policyNumber}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!response.ok) {
          throw new Error('Policy not found');
        }
        
        const policyData = await response.json();
        setPolicy(policyData);
        setDocuments(policyData.documents || []);
        
      } catch (err) {
        console.error('Error fetching policy:', err);
        setError(getUserFriendlyError(err.message));
      } finally {
        setLoading(false);
      }
    };

    if (policyNumber) {
      fetchPolicyDetails();
    }
  }, [policyNumber, getToken]);

  const handleViewDocument = (doc) => {
    setViewingDoc(doc);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}${doc.download_url}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const handleDownloadFullPacket = () => {
    const fullPacket = documents.find(d => d.document_type === 'full_packet');
    if (fullPacket) {
      handleDownloadDocument(fullPacket);
    }
  };

  const handleResendEmail = async () => {
    try {
      setSendingEmail(true);
      const token = getToken();
      await fetch(`${API_URL}/api/policy/resend-confirmation/${policyNumber}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (err) {
      console.error('Error resending email:', err);
    } finally {
      setSendingEmail(false);
    }
  };

  const isActivePolicy = (status) => {
    const s = status?.toLowerCase();
    return s === 'active' || s === 'issued' || s === 'policy_issued_complete' || s === 'policy_issued';
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    if (doc.document_type === 'full_packet') return false;
    
    const meta = documentMetadata[doc.document_type];
    const matchesCategory = activeCategory === 'all' || meta?.category === activeCategory;
    const matchesSearch = !searchQuery || 
      doc.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meta?.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // Category counts
  const categoryCounts = {
    all: documents.filter(d => d.document_type !== 'full_packet').length,
    essential: documents.filter(d => documentMetadata[d.document_type]?.category === 'essential').length,
    coverage: documents.filter(d => documentMetadata[d.document_type]?.category === 'coverage').length,
    legal: documents.filter(d => documentMetadata[d.document_type]?.category === 'legal').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#3B5BDB] animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading your policy documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0 rounded-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Policy Not Found</h2>
            <p className="text-slate-600 mb-6">We couldn't find a policy with number {policyNumber}.</p>
            <Button onClick={() => navigate('/portal')} className="bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const policyholder = policy?.policyholder || {};
  const vehicleInfo = policy?.vehicle_info || {};

  return (
    <PortalLayout
      pageTitle="Documents"
      policyNumber={policyNumber}
      userName={policyholder?.first_name}
    >
      <div className="bg-gradient-to-b from-[#E8EEFF] to-slate-50 min-h-full">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#3B5BDB] to-[#5B7FFF] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
            <button
              onClick={() => navigate('/portal')}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-medium mb-6 transition-colors group"
              data-testid="back-to-portal-btn"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Dashboard
            </button>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <FolderOpen className="h-6 w-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Document Center</h1>
                    <p className="text-white/80">Policy #{policyNumber}</p>
                  </div>
                </div>
                <p className="text-white/90 max-w-xl">
                  Access all your policy documents, coverage details, and important notices in one place.
                </p>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-white/70 text-xs uppercase tracking-wider">Coverage</p>
                  <p className="text-xl font-bold">{formatCurrency(policy?.coverage_amount)}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-white/70 text-xs uppercase tracking-wider">Status</p>
                  <p className="text-xl font-bold flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    Active
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-white/70 text-xs uppercase tracking-wider">Documents</p>
                  <p className="text-xl font-bold">{documents.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          
          {/* Download Full Packet CTA */}
          <Card className="mb-8 border-0 shadow-xl rounded-2xl overflow-hidden bg-gradient-to-r from-slate-900 to-slate-800">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#3B5BDB] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Complete Policy Packet</h3>
                    <p className="text-slate-400 text-sm">
                      Download all your policy documents in one convenient PDF file
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleDownloadFullPacket}
                  className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-semibold"
                  data-testid="download-full-packet-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download All Documents
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#3B5BDB] focus:ring-2 focus:ring-[#3B5BDB]/20 outline-none transition-all"
                data-testid="document-search"
              />
            </div>

            {/* Category Filters */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {[
                { key: 'all', label: 'All', icon: FolderOpen },
                { key: 'essential', label: 'Essential', icon: Sparkles },
                { key: 'coverage', label: 'Coverage', icon: Shield },
                { key: 'legal', label: 'Legal', icon: Lock }
              ].map(({ key, label, icon: CategoryIcon }) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                    activeCategory === key
                      ? 'bg-[#3B5BDB] text-white shadow-lg'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                  data-testid={`filter-${key}`}
                >
                  <CategoryIcon className="h-4 w-4" />
                  {label}
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeCategory === key ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    {categoryCounts[key]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Policy Summary Card */}
          <Card className="mb-6 shadow-xl border-0 rounded-2xl overflow-hidden" data-testid="policy-summary-card">
            <div className="bg-slate-50 px-6 py-4 border-b">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#3B5BDB]" />
                Policy Summary
              </h3>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Policyholder</p>
                  <p className="font-semibold text-slate-900">
                    {policyholder.first_name} {policyholder.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Vehicle</p>
                  <p className="font-semibold text-slate-900">
                    {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}{vehicleInfo.trim ? ` ${vehicleInfo.trim}` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Coverage</p>
                  <p className="font-bold text-[#3B5BDB]">{formatCurrency(policy?.coverage_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Deductible</p>
                  <p className="font-semibold text-slate-900">{formatCurrency(policy?.deductible_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Effective</p>
                  <p className="font-semibold text-slate-900">{formatDate(policy?.effective_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Expires</p>
                  <p className="font-semibold text-slate-900">{formatDate(policy?.expiration_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents Grid */}
          {filteredDocuments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {filteredDocuments.map((doc) => (
                <DocumentCard
                  key={doc.document_type}
                  document={doc}
                  onView={handleViewDocument}
                  onDownload={handleDownloadDocument}
                />
              ))}
            </div>
          ) : (
            <Card className="mb-8 border-0 shadow-md rounded-2xl">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">No documents found</h3>
                <p className="text-slate-500">Try adjusting your search or filter criteria</p>
              </CardContent>
            </Card>
          )}

          {/* State Disclosure (if applicable) */}
          {policy?.surplus_lines_disclosure?.requires_disclosure && (
            <Card className="mb-8 border-amber-200 bg-amber-50 shadow-md rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-2">
                      {policy.surplus_lines_disclosure.state_name} Surplus Lines Notice
                    </h3>
                    <p className="text-amber-900 text-sm leading-relaxed">
                      {policy.surplus_lines_disclosure.disclosure_text}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* Resend Email */}
            <Card className="shadow-lg border-0 rounded-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Resend Confirmation</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Email policy docs to {policyholder.email?.slice(0, 3)}***
                    </p>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleResendEmail}
                      disabled={sendingEmail}
                      className="rounded-xl w-full"
                      data-testid="resend-email-btn"
                    >
                      {sendingEmail ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : emailSent ? (
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      {emailSent ? 'Sent!' : 'Resend'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* File a Claim */}
            <Card className="shadow-lg border-0 rounded-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileCheck className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">File a Claim</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Start your diminished value claim process
                    </p>
                    <Button 
                      size="sm"
                      onClick={() => navigate(`/claims/file?policy=${policyNumber}`)}
                      className="rounded-xl w-full bg-[#3B5BDB] hover:bg-[#2F4BC7]"
                    >
                      Start Claim
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Get Help */}
            <Card className="shadow-lg border-0 rounded-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Need Help?</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Our team is ready to assist
                    </p>
                    <div className="space-y-1 text-sm">
                      <p className="text-slate-600">
                        <span className="font-medium">1-833-LOSSPAY</span>
                      </p>
                      <p className="text-slate-500 text-xs">support@losspay.com</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={!!viewingDoc}
        onClose={() => setViewingDoc(null)}
        document={viewingDoc}
        documentUrl={viewingDoc ? `${API_URL}${viewingDoc.download_url}` : ''}
        allDocuments={documents}
        onSelectDocument={setViewingDoc}
        onDownload={handleDownloadDocument}
      />
    </PortalLayout>
  );
}
